// La chat aperta di Conversazioni: chi risponde si cambia dall'intestazione (vale dal messaggio
// dopo), «Chiudi conversazione» la chiude. Scrivere in una conversazione chiusa la riprende.
import { useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import type { AgentProfile } from '../../lib/api';
import type { Session } from '../../types';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { MessageBubble } from '../../components/MessageBubble';
import { Pop } from '../../components/Pop';
import { Select } from '../../components/Select';
import { useStore } from '../../store/StoreProvider';
import { popTitle, usePopAvatars } from '../../lib/usePopAvatars';
import { traceBySession } from '../../store/selectors';
import { TraceList } from '../session/TraceList';

const LIVE = ['interpreting', 'awaiting_clarification', 'planning', 'running', 'awaiting_human'];

export function ChatPane({
  session: s,
  profiles,
  onBack,
}: {
  session: Session;
  profiles: AgentProfile[];
  onBack: () => void;
}) {
  const { state, dispatch, toast, openModal } = useStore();
  // la parte di debug sotto la chat: la traccia del pop (strumenti, esiti), come nella sessione
  const [debug, setDebug] = useState(false);
  const trace = traceBySession(state, s.id);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  const avatarOf = usePopAvatars();
  const messages = state.messages.filter((m) => m.sessionId === s.id);
  const live = LIVE.includes(s.state);
  const talker =
    profiles.find((p) => p.id === s.chatProfile) ?? profiles.find((p) => p.coordinator);
  const working = s.state === 'running';

  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  const fail = (title: string) => (e: unknown) =>
    toast({
      kind: 'danger',
      title,
      body: e instanceof ApiError && e.detail ? e.detail : 'Riprova fra poco.',
    });

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.reply(s.id, { text });
      setDraft('');
      if (!live) dispatch({ type: 'session/patch', id: s.id, patch: { state: 'running' } });
    } catch (e) {
      fail('Messaggio non inviato')(e);
    } finally {
      setBusy(false);
    }
  };

  const choose = (profile: string) => {
    dispatch({ type: 'session/patch', id: s.id, patch: { chatProfile: profile } });
    if (live) void api.reply(s.id, { profile }).catch(fail('Non riesco a cambiare pop'));
  };

  const close = () =>
    void api.reply(s.id, { end: true }).catch(fail('Non riesco a chiudere la conversazione'));

  return (
    <section className="cv-chat" aria-label="Conversazione">
      <header className="cv-chat-head">
        <Button
          variant="icon"
          icon="arrow-left"
          aria-label="Tutte le conversazioni"
          onClick={onBack}
          className="cv-back"
        />
        <div className="cv-title">
          <div className="t-body cv-ellipsis" style={{ fontWeight: 600 }}>
            {s.title}
          </div>
          <div className="t-caption dim">
            {live ? 'Conversazione · nessun piano, nessun team' : 'Chiusa · scrivendo riparte'}
          </div>
        </div>
        <span className="t-caption dim cv-who">Risponde</span>
        {talker && <Pop avatar={talker.avatar} size={28} />}
        <Select
          label="Pop che risponde"
          value={talker?.id ?? ''}
          onChange={choose}
          options={profiles.map((p) => ({ value: p.id, label: p.label }))}
          width={170}
        />
        {live && (
          <Button variant="secondary" onClick={close}>
            Chiudi conversazione
          </Button>
        )}
        <Button
          variant="ghost"
          icon="external-link"
          onClick={() =>
            openModal({
              kind: 'json',
              title: 'Dettaglio JSON',
              payload: { id: s.id, json: JSON.stringify(s, null, 2) },
            })
          }
        >
          JSON
        </Button>
      </header>
      <div className="cv-messages">
        {messages.map((m) => (
          <MessageBubble
            key={m.id}
            m={m}
            laneColor={s.color}
            avatar={avatarOf(m.authorLabel)}
            author={m.authorLabel ? popTitle(m.authorLabel, profiles) : undefined}
          />
        ))}
        {working && (
          <div className="t-caption dim" aria-live="polite" style={{ display: 'flex', gap: 6 }}>
            <span className="anim-spin" style={{ display: 'inline-grid' }}>
              <Icon name="loader" size={14} />
            </span>{' '}
            {talker?.label ?? 'Il pop'} sta rispondendo…
          </div>
        )}
        <div ref={end} />
      </div>
      <section className={`cv-debug${debug ? ' is-open' : ''}`} aria-label="Traccia">
        <button
          type="button"
          className="cv-debug-toggle"
          aria-expanded={debug}
          onClick={() => setDebug((v) => !v)}
        >
          <Icon name={debug ? 'chevron-down' : 'chevron-up'} size={14} />
          Traccia · {trace.length}
        </button>
        {debug && (
          <div className="cv-debug-body">
            <TraceList trace={trace} />
          </div>
        )}
      </section>
      {state.role === 'viewer' ? (
        <div className="cv-composer t-caption faint">
          Sola lettura: il tuo profilo è da visitatore.
        </div>
      ) : (
        <form
          className="cv-composer"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <textarea
            aria-label={`Scrivi a ${talker?.label ?? 'il pop'}`}
            placeholder={`Scrivi a ${talker?.label ?? 'il pop'}…`}
            value={draft}
            rows={2}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <Button
            variant="primary"
            icon="send"
            loading={busy}
            disabled={!draft.trim()}
            type="submit"
            aria-label="Invia"
          />
        </form>
      )}
    </section>
  );
}
